import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import ebdrMine from "@/assets/ebdr-mine.webp";

const PATENTS = [
  { country: "CL", num: "60.331" },
  { country: "AU", num: "2017226772" },
  { country: "PE", num: "11660" },
];

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const HeroManifesto = () => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setLoaded(true), 60);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <section className="relative flex items-center overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
      <div className="absolute inset-0 hairline-grid opacity-60 pointer-events-none" aria-hidden />
      <div
        className="absolute -top-32 -right-24 w-[38rem] h-[38rem] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--ember) / 0.18) 0%, transparent 68%)" }}
        aria-hidden
      />

      <div className="container mx-auto px-4 relative z-10 w-full">
        <div className={`transition-all duration-700 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="eyebrow">{t("landing.v2.heroBadge")}</span>
        </div>

        <h1
          className={`mt-6 text-[3.5rem] leading-[0.86] sm:text-[5.5rem] md:text-[8rem] lg:text-[10.5rem] uppercase transition-all duration-1000 ${
            loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="block text-foreground">{t("landing.v2.heroTitle1")}</span>
          <span className="block text-primary">{t("landing.v2.heroTitle2")}</span>
        </h1>

        <div className="grid lg:grid-cols-12 gap-10 mt-10 items-end">
          <div
            className={`lg:col-span-5 transition-all duration-1000 delay-200 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              {t("landing.v2.heroLead")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Button size="lg" className="rounded-none px-8 uppercase tracking-widest" onClick={() => scrollTo("contacto")}>
                {t("landing.v2.heroCta1")} <ArrowRight size={16} className="ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-none px-8 uppercase tracking-widest border-foreground/25"
                onClick={() => scrollTo("inversion")}
              >
                {t("landing.v2.heroCta2")}
              </Button>
            </div>

            <div className="mt-10">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70">
                {t("landing.v2.heroPatents")}
              </span>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
                {PATENTS.map(({ country, num }) => (
                  <span key={country} className="font-mono text-xs text-foreground/80 border-l border-primary/60 pl-3">
                    {country} · {num}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div
            className={`lg:col-span-7 transition-all duration-1000 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <figure className="relative ember-glow">
              <img
                src={ebdrMine}
                alt={t("landing.v2.seoAlt")}
                className="w-full h-auto object-cover aspect-[16/10]"
                loading="eager"
                width={1600}
                height={1000}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent pointer-events-none" />
              <figcaption className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80 bg-background/60 px-3 py-1">
                {t("landing.v2.heroCaption")}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroManifesto;
